import { takeEvery, call, put } from "redux-saga/effects";
import {
  fetchAssessmentsStart,
  fetchAssessmentsSuccess,
  fetchAssessmentsFail,
  fetchSingleAssessmentStart,
  fetchSingleAssessmentSuccess,
  fetchSingleAssessmentFail,
  fetchRandomTraitQuestionsStart,
  fetchRandomTraitQuestionsSuccess,
  fetchRandomTraitQuestionsFail,
  fetchRandomProjectiveQuestionsStart,
  fetchRandomProjectiveQuestionsSuccess,
  fetchRandomProjectiveQuestionsFail,
  createAssessmentStart,
  createAssessmentSuccess,
  createAssessmentFail,
  updateAssessmentSuccess,
  updateAssessmentFail,
  updateAssessmentStart,
  fetchQuestionsSuccess,
  fetchQuestionsFail,
  fetchQuestionsStart,
  createQuestionSuccess,
  createQuestionFail,
  updateQuestionSuccess,
  updateQuestionFail,
  createQuestionStart,
  updateQuestionStart,
  fetchReAssessmentRequestsSuccess,
  fetchReAssessmentRequestsFail,
  updateReAssessmentRequestSuccess,
  updateReAssessmentRequestFail,
  fetchReAssessmentRequestsStart,
  updateReAssessmentRequestStart,
  importAssessmentsSuccess,
  importAssessmentsFail,
  importAssessmentsStart,
} from "../slices/assessmentsSlice";
import {
  getRequest,
  postRequest,
  putRequest,
  uploadFiles,
} from "../../network";
import {
  createAssessment,
  getAssessments,
  getAllQuestions,
  getAssessmentById,
  randomTraitQuestion,
  randomProjectiveQuestion,
  updateAssessment,
  addQuestion,
  updateQuestion,
  getReAssessmentRequests,
  updateReAssessmentRequest,
  importAssessments,
} from "../apis";

function* assessmentsSaga(action) {
  const {
    endpoint,
    method = "get",
    successAction,
    failAction,
    payload = null,
    callback = null,
  } = action.payload;
  const requestFunction =
    method === "post"
      ? postRequest
      : method === "put"
        ? putRequest
        : getRequest;
  try {
    const response = yield call(requestFunction, endpoint, payload);
    if (callback && typeof callback === "function") {
      yield call(callback, true);
    }
    yield put(successAction(response));
  } catch (error) {
    if (callback && typeof callback === "function") {
      let errorMessage = "Something went wrong";
      if (error.response && error.response.data) {
        const data = error.response.data;
        if (data.message && data.errors) {
          errorMessage = `${data.message}: ${data.errors.join(", ")}`;
        } else if (data.message) {
          errorMessage = data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      yield call(callback, errorMessage);
    }
    yield put(failAction(error.message));
  }
}

function* uploadAssesmentsFileSaga(action) {
  const { endpoint, successAction, failAction, payload, callback } =
    action.payload;
const file = payload?.file || payload?.payload;
  try {
    const response = yield call(uploadFiles, endpoint, file, {}, { timeout: 600000 });

    if (callback && typeof callback === "function") {
      yield call(callback, true, response);
    }
    yield put(successAction(response));
  } catch (error) {
    const message =
      error.response?.data?.message || error.message || "Upload failed";

    if (callback && typeof callback === "function") {
      yield call(callback, false, message);
    }
    yield put(failAction(message));
  }
}
function* fetchAllAssessmentsSaga(action) {
  yield assessmentsSaga({
    payload: {
      endpoint: getAssessments,
      successAction: fetchAssessmentsSuccess,
      failAction: fetchAssessmentsFail,
      payload: action.payload,
    },
  });
}

function* fetchQuestionsSaga(action) {
  yield assessmentsSaga({
    payload: {
      endpoint: getAllQuestions,
      successAction: fetchQuestionsSuccess,
      failAction: fetchQuestionsFail,
      payload: action.payload,
    },
  });
}

function* fetchSingleAssessmentSaga(action) {
  yield assessmentsSaga({
    payload: {
      endpoint: getAssessmentById.replace(":id", action.payload.id),
      successAction: fetchSingleAssessmentSuccess,
      failAction: fetchSingleAssessmentFail,
    },
  });
}

function* fetchRandomTraitQuestionsSaga() {
  yield assessmentsSaga({
    payload: {
      endpoint: randomTraitQuestion,
      successAction: fetchRandomTraitQuestionsSuccess,
      failAction: fetchRandomTraitQuestionsFail,
    },
  });
}

function* fetchRandomProjectiveQuestionsSaga() {
  yield assessmentsSaga({
    payload: {
      endpoint: randomProjectiveQuestion,
      successAction: fetchRandomProjectiveQuestionsSuccess,
      failAction: fetchRandomProjectiveQuestionsFail,
    },
  });
}

function* createAssessmentSaga(action) {
  yield assessmentsSaga({
    payload: {
      endpoint: createAssessment,
      method: "post",
      payload: action.payload.data,
      callback: action.payload.callback,
      successAction: createAssessmentSuccess,
      failAction: createAssessmentFail,
    },
  });
}

function* updateAssessmentSaga(action) {
  yield assessmentsSaga({
    payload: {
      endpoint: updateAssessment.replace(":id", action.payload.id),
      method: "put",
      payload: action.payload.data,
      callback: action.payload.callback,
      successAction: updateAssessmentSuccess,
      failAction: updateAssessmentFail,
    },
  });
}

function* createQuestionSaga(action) {
  yield assessmentsSaga({
    payload: {
      endpoint: addQuestion,
      method: "post",
      payload: action.payload.data,
      callback: action.payload.callback,
      successAction: createQuestionSuccess,
      failAction: createQuestionFail,
    },
  });
}

function* updateQuestionSaga(action) {
  yield assessmentsSaga({
    payload: {
      endpoint: updateQuestion.replace(":id", action.payload.id),
      method: "put",
      payload: action.payload.data,
      callback: action.payload.callback,
      successAction: updateQuestionSuccess,
      failAction: updateQuestionFail,
    },
  });
}

function* fetchAllReAssessmentRequestsSaga(action) {
  yield assessmentsSaga({
    payload: {
      endpoint: getReAssessmentRequests,
      successAction: fetchReAssessmentRequestsSuccess,
      failAction: fetchReAssessmentRequestsFail,
      payload: action.payload,
    },
  });
}

function* updateReAssessmentRequestSaga(action) {
  yield assessmentsSaga({
    payload: {
      endpoint: updateReAssessmentRequest.replace(":id", action.payload.id),
      method: "put",
      payload: action.payload.data,
      callback: action.payload.callback,
      successAction: updateReAssessmentRequestSuccess,
      failAction: updateReAssessmentRequestFail,
    },
  });
}

function* importAssessmentsSaga(action) {
  yield uploadAssesmentsFileSaga({
    payload: {
      endpoint: importAssessments,
      method: "post",
      payload: action.payload,
      callback: action.payload.callback,
      successAction: importAssessmentsSuccess,
      failAction: importAssessmentsFail,
    },
  });
}

export function* watchAssessments() {
  yield takeEvery(fetchAssessmentsStart.type, fetchAllAssessmentsSaga);
  yield takeEvery(fetchQuestionsStart.type, fetchQuestionsSaga);
  yield takeEvery(fetchSingleAssessmentStart.type, fetchSingleAssessmentSaga);
  yield takeEvery(
    fetchRandomTraitQuestionsStart.type,
    fetchRandomTraitQuestionsSaga
  );
  yield takeEvery(
    fetchRandomProjectiveQuestionsStart.type,
    fetchRandomProjectiveQuestionsSaga
  );
  yield takeEvery(createAssessmentStart.type, createAssessmentSaga);
  yield takeEvery(updateAssessmentStart.type, updateAssessmentSaga);
  yield takeEvery(createQuestionStart.type, createQuestionSaga);
  yield takeEvery(updateQuestionStart.type, updateQuestionSaga);
  yield takeEvery(
    fetchReAssessmentRequestsStart.type,
    fetchAllReAssessmentRequestsSaga
  );
  yield takeEvery(
    updateReAssessmentRequestStart.type,
    updateReAssessmentRequestSaga
  );
  yield takeEvery(importAssessmentsStart.type, importAssessmentsSaga);
}
